/**
 * Formulier voor Nieuwe/Edit Werknemer
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { werknemersService, WerknemerCreate, ContractType, Role } from '../../services/werknemers.service';
import ErrorDialog from '../../components/ErrorDialog';
import Toast from '../../components/Toast';
import '../relations/Relations.css';

interface WerknemerFormProps {
  werknemerId?: number;
}

const WerknemerForm = ({ werknemerId }: WerknemerFormProps) => {
  const navigate = useNavigate();
  const isEditMode = !!werknemerId;

  const [formData, setFormData] = useState<WerknemerCreate>({
    personeelsnummer: '',
    aanhef: '',
    voornaam: '',
    tussenvoegsel: '',
    achternaam: '',
    email: '',
    telefoon: '',
    mobiel: '',
    straat: '',
    huisnummer: '',
    postcode: '',
    plaats: '',
    land: 'Nederland',
    geboortedatum: '',
    bsn: '',
    contract_type: undefined,
    datum_in_dienst: '',
    datum_uit_dienst: '',
    contract_einddatum: '',
    uren_per_week: undefined,
    functie: '',
    afdeling: '',
    cao: '',
    bruto_uurloon: undefined,
    basisuurloon: undefined,
    reiskostenvergoeding: undefined,
    expertises: '',
    certificaten: '',
    noodcontact_naam: '',
    noodcontact_telefoon: '',
    noodcontact_relatie: '',
    notities: '',
    actief: true,
    // Login gegevens
    username: '',
    password: '',
    rol_id: undefined,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);

  const loadWerknemer = useCallback(async () => {
    if (!werknemerId) return;

    try {
      setLoading(true);
      const werknemer = await werknemersService.getById(werknemerId);
      setFormData(werknemer);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [werknemerId]);

  useEffect(() => {
    if (isEditMode) {
      loadWerknemer();
    }
  }, [isEditMode, loadWerknemer]);

  useEffect(() => {
    const loadRoles = async () => {
      try {
        console.log('Loading roles...');
        const rolesData = await werknemersService.getRoles();
        console.log('Roles loaded:', rolesData);
        setRoles(rolesData);
      } catch (err) {
        console.error('Fout bij laden van rollen:', err);
        // Als er een fout is, probeer het nog een keer na 1 seconde
        setTimeout(async () => {
          try {
            const rolesData = await werknemersService.getRoles();
            setRoles(rolesData);
          } catch {
            // Negeer tweede poging fout
          }
        }, 1000);
      }
    };
    loadRoles();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    const inputType = (e.target as HTMLInputElement).type;

    setFormData((prev) => ({
      ...prev,
      [name]:
        inputType === 'number'
          ? value === ''
            ? undefined
            : parseFloat(value)
          : inputType === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (isEditMode && werknemerId) {
        await werknemersService.update(werknemerId, formData);
        setSuccess('Werknemer succesvol bijgewerkt!');
      } else {
        await werknemersService.create(formData);
        setSuccess('Werknemer succesvol aangemaakt!');
      }

      setTimeout(() => {
        navigate('/werknemers');
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return <div className="loading">Laden...</div>;
  }

  return (
    <div className="relation-form">
      <div className="page-header">
        <h1>{isEditMode ? 'Werknemer Bewerken' : 'Nieuwe Werknemer'}</h1>
      </div>

      <Toast message={success} type="success" onClose={() => setSuccess(null)} />

      <form onSubmit={handleSubmit}>
        {/* Persoonlijke Informatie */}
        <section className="form-section">
          <h2>Persoonlijke Informatie</h2>
          <div className="form-grid">
            <div className="form-row">
              <div className="form-group narrow">
                <label htmlFor="aanhef">Aanhef</label>
                <select
                  id="aanhef"
                  name="aanhef"
                  value={formData.aanhef}
                  onChange={handleChange}
                >
                  <option value="">Selecteer...</option>
                  <option value="Dhr.">Dhr.</option>
                  <option value="Mevr.">Mevr.</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="voornaam">
                  Voornaam <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="voornaam"
                  name="voornaam"
                  value={formData.voornaam}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group narrow">
                <label htmlFor="tussenvoegsel">Tussenvoegsel</label>
                <input
                  type="text"
                  id="tussenvoegsel"
                  name="tussenvoegsel"
                  value={formData.tussenvoegsel}
                  onChange={handleChange}
                  placeholder="van, de, etc."
                />
              </div>

              <div className="form-group">
                <label htmlFor="achternaam">
                  Achternaam <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="achternaam"
                  name="achternaam"
                  value={formData.achternaam}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="geboortedatum">Geboortedatum</label>
              <input
                type="date"
                id="geboortedatum"
                name="geboortedatum"
                value={formData.geboortedatum || ''}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="bsn">BSN</label>
              <input
                type="text"
                id="bsn"
                name="bsn"
                value={formData.bsn}
                onChange={handleChange}
                placeholder="Burgerservicenummer"
              />
            </div>

            <div className="form-group">
              <label htmlFor="personeelsnummer">Personeelsnummer</label>
              <input
                type="text"
                id="personeelsnummer"
                name="personeelsnummer"
                value={formData.personeelsnummer}
                onChange={handleChange}
              />
            </div>
          </div>
        </section>

        {/* Adres Gegevens */}
        <section className="form-section">
          <h2>Adres Gegevens</h2>
          <div className="form-grid">
            <div className="form-group span-2">
              <label htmlFor="straat">Straat</label>
              <input
                type="text"
                id="straat"
                name="straat"
                value={formData.straat}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="huisnummer">Huisnummer</label>
              <input
                type="text"
                id="huisnummer"
                name="huisnummer"
                value={formData.huisnummer}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="postcode">Postcode</label>
              <input
                type="text"
                id="postcode"
                name="postcode"
                value={formData.postcode}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="plaats">Plaats</label>
              <input
                type="text"
                id="plaats"
                name="plaats"
                value={formData.plaats}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="land">Land</label>
              <input
                type="text"
                id="land"
                name="land"
                value={formData.land}
                onChange={handleChange}
              />
            </div>
          </div>
        </section>

        {/* Contact Informatie */}
        <section className="form-section">
          <h2>Contact Informatie</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="telefoon">Telefoon</label>
              <input
                type="tel"
                id="telefoon"
                name="telefoon"
                value={formData.telefoon}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="mobiel">Mobiel</label>
              <input
                type="tel"
                id="mobiel"
                name="mobiel"
                value={formData.mobiel}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>
          </div>
        </section>

        {/* Dienstverband */}
        <section className="form-section">
          <h2>Dienstverband</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="contract_type">Contract Type</label>
              <select
                id="contract_type"
                name="contract_type"
                value={formData.contract_type || ''}
                onChange={handleChange}
              >
                <option value="">Selecteer...</option>
                <option value={ContractType.VAST}>Vast</option>
                <option value={ContractType.TIJDELIJK}>Tijdelijk</option>
                <option value={ContractType.OPROEP}>Oproep</option>
                <option value={ContractType.ZZP}>ZZP</option>
                <option value={ContractType.UITZEND}>Uitzend</option>
                <option value={ContractType.STAGE}>Stage</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="datum_in_dienst">Datum In Dienst</label>
              <input
                type="date"
                id="datum_in_dienst"
                name="datum_in_dienst"
                value={formData.datum_in_dienst || ''}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="contract_einddatum">Contract Einddatum</label>
              <input
                type="date"
                id="contract_einddatum"
                name="contract_einddatum"
                value={formData.contract_einddatum || ''}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="datum_uit_dienst">Datum Uit Dienst</label>
              <input
                type="date"
                id="datum_uit_dienst"
                name="datum_uit_dienst"
                value={formData.datum_uit_dienst || ''}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="uren_per_week">Uren per Week</label>
              <input
                type="number"
                id="uren_per_week"
                name="uren_per_week"
                value={formData.uren_per_week || ''}
                onChange={handleChange}
                min="0"
                max="60"
                step="0.5"
              />
            </div>
          </div>
        </section>


        {/* CAO en Loon */}
        <section className="form-section">
          <h2>CAO en Loon</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="cao">CAO</label>
              <input
                type="text"
                id="cao"
                name="cao"
                value={formData.cao}
                onChange={handleChange}
                placeholder="Bijv. Metaal en Techniek"
              />
            </div>

            <div className="form-group">
              <label htmlFor="bruto_uurloon">Bruto Uurloon</label>
              <input
                type="number"
                id="bruto_uurloon"
                name="bruto_uurloon"
                value={formData.bruto_uurloon || ''}
                onChange={handleChange}
                min="0"
                step="0.01"
                placeholder="€"
              />
            </div>

            <div className="form-group">
              <label htmlFor="basisuurloon">Basisuurloon</label>
              <input
                type="number"
                id="basisuurloon"
                name="basisuurloon"
                value={formData.basisuurloon || ''}
                onChange={handleChange}
                min="0"
                step="0.01"
                placeholder="€"
              />
            </div>

            <div className="form-group">
              <label htmlFor="reiskostenvergoeding">Reiskostenvergoeding</label>
              <input
                type="number"
                id="reiskostenvergoeding"
                name="reiskostenvergoeding"
                value={formData.reiskostenvergoeding || ''}
                onChange={handleChange}
                min="0"
                step="0.01"
                placeholder="€"
              />
            </div>
          </div>
        </section>

        {/* Expertises en Certificaten */}
        <section className="form-section">
          <h2>Expertises en Certificaten</h2>
          <div className="form-grid">
            <div className="form-group span-2">
              <label htmlFor="expertises">Expertises</label>
              <textarea
                id="expertises"
                name="expertises"
                value={formData.expertises}
                onChange={handleChange}
                rows={3}
                placeholder="Bijv. Houtbewerking, Lamineren, Motoronderhoud"
              />
            </div>

            <div className="form-group span-2">
              <label htmlFor="certificaten">Certificaten</label>
              <textarea
                id="certificaten"
                name="certificaten"
                value={formData.certificaten}
                onChange={handleChange}
                rows={3}
                placeholder="Bijv. VCA, BHV, Heftruck"
              />
            </div>
          </div>
        </section>

        {/* Noodcontact */}
        <section className="form-section">
          <h2>Noodcontact</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="noodcontact_naam">Naam</label>
              <input
                type="text"
                id="noodcontact_naam"
                name="noodcontact_naam"
                value={formData.noodcontact_naam}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="noodcontact_telefoon">Telefoon</label>
              <input
                type="tel"
                id="noodcontact_telefoon"
                name="noodcontact_telefoon"
                value={formData.noodcontact_telefoon}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="noodcontact_relatie">Relatie</label>
              <select
                id="noodcontact_relatie"
                name="noodcontact_relatie"
                value={formData.noodcontact_relatie}
                onChange={handleChange}
              >
                <option value="">Selecteer...</option>
                <option value="Partner">Partner</option>
                <option value="Ouder">Ouder</option>
                <option value="Kind">Kind</option>
                <option value="Broer/Zus">Broer/Zus</option>
                <option value="Vriend(in)">Vriend(in)</option>
                <option value="Anders">Anders</option>
              </select>
            </div>
          </div>
        </section>

        {/* Login Gegevens */}
        <section className="form-section">
          <h2>Login Gegevens</h2>
          <p style={{ marginBottom: '1rem', color: '#666' }}>
            Optioneel: geef deze werknemer toegang tot het systeem
          </p>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="username">Gebruikersnaam</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username || ''}
                onChange={handleChange}
                placeholder="Laat leeg voor geen toegang"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">
                {isEditMode ? 'Nieuw Wachtwoord' : 'Wachtwoord'}
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password || ''}
                onChange={handleChange}
                placeholder={isEditMode ? 'Laat leeg om te behouden' : 'Minimaal 8 tekens'}
                minLength={8}
              />
              {isEditMode && (
                <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
                  Laat leeg om het huidige wachtwoord te behouden
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="rol_id">Rol</label>
              <select
                id="rol_id"
                name="rol_id"
                value={formData.rol_id || ''}
                onChange={handleChange}
              >
                <option value="">Selecteer rol...</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.naam}
                  </option>
                ))}
              </select>
              <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
                Admin rol geeft volledige toegang tot alle functies
              </small>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="actief"
                  checked={formData.actief}
                  onChange={handleChange}
                />
                <span>Actief</span>
              </label>
              <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
                Inactieve werknemers worden niet getoond in overzichten
              </small>
            </div>

          </div>
        </section>

        {/* Notities */}
        <section className="form-section">
          <h2>Notities</h2>
          <div className="form-group">
            <textarea
              id="notities"
              name="notities"
              value={formData.notities}
              onChange={handleChange}
              rows={4}
              placeholder="Eventuele opmerkingen..."
            />
          </div>
        </section>

        <div className="form-actions">
          <button
            type="button"
            className="btn-lg btn-secondary"
            onClick={() => navigate('/werknemers')}
          >
            Annuleren
          </button>
          <button type="submit" className="btn-lg btn-success" disabled={loading}>
            {loading ? 'Bezig...' : isEditMode ? 'Bijwerken' : 'Aanmaken'}
          </button>
        </div>
      </form>

      <ErrorDialog
        isOpen={!!error}
        message={error || ''}
        onClose={() => setError(null)}
      />
    </div>
  );
};

export default WerknemerForm;
